import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ROUTES } from '@/utils/constants/routesConstants';
import { DatePickerInput, LoadingSpinner } from 'pepsico-ds';
import {
  useStoreCount,
  useMissingVisitListCount,
  useMissingSegmentsCount,
  useOktaStatusCount
} from '@/services/Onboarding/onboardingService';

import OnboardingActionCard from '@/components/common/OnboardingActionCard/OnboardingActionCard';
import StatsDisplay from '@/components/common/StatsDisplay/StatsDisplay';
import OktaStatusChart from '@/components/common/OktaStatusChart/OktaStatusChart';
import DateRangeSelector from '@/components/common/DateRangeSelector/DateRangeSelector';
import { formatDateForUrl } from '@/helpers/formatDateForUrl';

export default function OnboardingStatusOverviewPage () {
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState({
    startDate: new Date(2023, 0, 1), // Jan 1, 2023
    endDate: (() => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    })(),
    isValid: true
  });

  const shouldLoadData = useMemo(() => {
    return !!dateRange.startDate && !!dateRange.endDate && dateRange.isValid;
  }, [dateRange]);

  // Fetch data using hooks, passing date states
  const {
    data: storeCountData,
    isLoading: isLoadingStoreCount,
    isError: isErrorStoreCount
  } = useStoreCount(dateRange.startDate, dateRange.endDate, {
    enabled: shouldLoadData
  });

  const {
    data: visitListCountData,
    isLoading: isLoadingVisitListCount,
    isError: isErrorVisitListCount
  } = useMissingVisitListCount(dateRange.startDate, dateRange.endDate, {
    enabled: shouldLoadData
  });

  const {
    data: segmentsCountData,
    isLoading: isLoadingSegmentsCount,
    isError: isErrorSegmentsCount
  } = useMissingSegmentsCount(dateRange.startDate, dateRange.endDate, {
    enabled: shouldLoadData
  });

  const {
    data: oktaStatusData,
    isLoading: isLoadingOktaStatus,
    isError: isErrorOktaStatus
  } = useOktaStatusCount(dateRange.startDate, dateRange.endDate, {
    enabled: shouldLoadData
  });

  // --- Data Transformation ---
  // Memoize transformed data to avoid recalculations on every render
  const detailedStatsItems = useMemo(() => {
    // Determine if any detailed stat is loading
    const isAnyDetailedLoading =
      isLoadingVisitListCount ||
      isLoadingSegmentsCount;
    // Determine if any detailed stat has an error
    const isAnyDetailedError =
      isErrorVisitListCount || isErrorSegmentsCount;

    const items = [
      {
        value: isErrorVisitListCount
          ? 'Error'
          : parseInt(visitListCountData?.stores_with_no_visit_list) ?? 0,
        isLoading: isLoadingVisitListCount,
        color: 'text-red-500',
        label: 'Missing Visit List'
      },
      {
        value: isErrorSegmentsCount ? 'Error' : segmentsCountData ?? 0,
        isLoading: isLoadingSegmentsCount,
        color: 'text-[#FFCB66]',
        label: 'Missing Segment(s)'
      }
    ];

    // Calculate total issues (only count numbers, ignore 'Error', null, and loading states)
    const totalIssuesValue = items.reduce((sum, item) => {
      // Only sum up if the item has a valid numeric value (ignore errors and loading)
      return sum + (typeof item.value === 'number' ? item.value : 0);
    }, 0);

    // Return items and the calculated total value (isLoading handled separately)
    return {
      items,
      totalIssuesValue,
      isAnyDetailedLoading,
      isAnyDetailedError
    };
  }, [
    visitListCountData,
    segmentsCountData,
    isLoadingVisitListCount,
    isLoadingSegmentsCount,
    isErrorVisitListCount,
    isErrorSegmentsCount
  ]);

  // Memoize main stats items
  const mainStatsItems = useMemo(
    () => [
      {
        value: isErrorStoreCount ? 'Error' : storeCountData ?? 0,
        isLoading: isLoadingStoreCount, // Add isLoading prop
        label: 'Stores'
      },
      {
        // Use the calculated total issues value from detailedStatsItems
        value: detailedStatsItems.totalIssuesValue,
        // Loading state is true if any detailed item is loading
        isLoading: detailedStatsItems.isAnyDetailedLoading,
        label: 'Total Issues'
      }
    ],
    [
      storeCountData,
      isLoadingStoreCount,
      isErrorStoreCount,
      detailedStatsItems.totalIssuesValue,
      detailedStatsItems.isAnyDetailedLoading
    ]
  );

  // Helper function to create URL with date parameters
  const navigateWithDates = route => {
    const queryParams = new URLSearchParams();
    if (dateRange.startDate)
      queryParams.append('startDate', formatDateForUrl(dateRange.startDate));
    if (dateRange.endDate)
      queryParams.append('endDate', formatDateForUrl(dateRange.endDate));
    const queryString = queryParams.toString();
    navigate(`${route}${queryString ? `?${queryString}` : ''}`);
  };

  return (
    <div className='flex flex-col w-full min-h-full'>
      <div className='flex flex-row p-4 pt-0 px-0'>
        <h3 className='text-[34px] font-bold'>Onboarding Status</h3>
      </div>

      <DateRangeSelector
        onDatesChange={setDateRange}
        initialStartDate={dateRange.startDate}
        initialEndDate={dateRange.endDate}
      />

      {!shouldLoadData ? (
        <div className='text-center p-8'>
          <p>Please select both start and end dates to load the data.</p>
        </div>
      ) : (
        <div className='flex flex-row w-full min-h-full gap-4'>
          <div className='w-3/4 flex-col rounded-lg pb-4'>
            <div className='bg-[#1d1d1d] rounded-lg p-4 mt-4'>
              <>
                <div className='border-[#4E4E4E] border-[1px] rounded-lg p-2 px-2 w-fit max-w-[500px]'>
                  {/* Pass updated mainStatsItems (now includes isLoading) */}
                  <StatsDisplay items={mainStatsItems} />
                </div>

                <div className='my-4'>
                  {/* Pass updated detailedStatsItems (now includes isLoading) */}
                  <StatsDisplay size='small' items={detailedStatsItems.items} />
                </div>

                {/* Add isLoading prop for Product & Price List if/when available */}
                <OnboardingActionCard
                  title='Visit List'
                  subtext="No Visit List assigned to stores. BDR's will not be able to see their stores. "
                  storeCount={
                    visitListCountData?.stores_with_no_visit_list ?? null
                  }
                  isLoading={isLoadingVisitListCount}
                  onTakeAction={() =>
                    navigateWithDates(
                      ROUTES.ONBOARDING_STATUS_MISSING_VISIT_LIST
                    )
                  }
                />

                {/* Add isLoading prop for MOA if/when available */}
                <OnboardingActionCard
                  title='MOA'
                  subtext='No MOA assigned to stores. '
                  storeCount={visitListCountData?.stores_missing_moa ?? 0}
                  isLoading={isLoadingVisitListCount}
                  onTakeAction={() =>
                    navigateWithDates(ROUTES.ONBOARDING_STATUS_MISSING_MOA)
                  }
                />

                {/* Add isLoading prop for Cut Off Time if/when available */}
                <OnboardingActionCard
                  title='Cut Off Time'
                  subtext='No Cut Off Time assigned to stores.'
                  storeCount={
                    visitListCountData?.stores_missing_cutoffTime ?? 0
                  }
                  isLoading={isLoadingVisitListCount}
                  onTakeAction={() =>
                    navigateWithDates(
                      ROUTES.ONBOARDING_STATUS_MISSING_CUT_OFF_TIME
                    )
                  }
                />

                {/* Add isLoading prop for Delivery Service if/when available */}
                <OnboardingActionCard
                  title='Delivery Service'
                  subtext='No Delivery Service time assigned to stores. '
                  storeCount={
                    visitListCountData?.stores_missing_deliveryService ?? 0
                  }
                  isLoading={isLoadingVisitListCount}
                  onTakeAction={() =>
                    navigateWithDates(
                      ROUTES.ONBOARDING_STATUS_MISSING_DELIVERY_SERVICE
                    )
                  }
                />

                <OnboardingActionCard
                  title='Segments'
                  subtext='Customers not assigned to segments.'
                  storeCount={segmentsCountData ?? null}
                  isLoading={isLoadingSegmentsCount}
                  onTakeAction={() =>
                    navigateWithDates(ROUTES.ONBOARDING_STATUS_MISSING_SEGMENTS)
                  }
                />
              </>
              {/* )} */}
            </div>
          </div>

          <div className='w-1/4 '>
            <div className='mt-[16px]'>
              {/* Display error message if there's an error loading chart data */}
              {isErrorOktaStatus && !isLoadingOktaStatus && (
                <div className='text-red-500 p-4 text-center'>
                  Error loading chart data.
                </div>
              )}

              {/* Render chart or loading/no data message based on specific Okta status state */}
              {isLoadingOktaStatus ? (
                <div className='flex justify-center items-center h-[200px]'>
                  {' '}
                  {/* Adjust height as needed */}
                  <LoadingSpinner size={{ height: '40px', width: '40px' }} />
                </div>
              ) : !isErrorOktaStatus && oktaStatusData ? (
                <OktaStatusChart
                  totalStores={oktaStatusData?.totalCustomers ?? 0}
                  activeStores={oktaStatusData?.activeStores ?? 0}
                  lockedOutStores={oktaStatusData?.lockedOutStores ?? 0}
                  suspendedStores={oktaStatusData?.suspendedStores ?? 0}
                  deprovisionedStores={oktaStatusData?.deprovisionedStores ?? 0}
                  inactiveStores={oktaStatusData?.incompleteRegistrations ?? 0}
                  onExpand={() => console.log('Expand chart')}
                  onClick={() =>
                    navigateWithDates(ROUTES.ONBOARDING_STATUS_OKTA_ACTIVE)
                  }
                  // isLoading is handled above, so we don't pass it here anymore
                />
              ) : (
                // If there's no error, not loading, and no data, show 'No data' message
                !isErrorOktaStatus && (
                  <div className='text-white p-4 text-center'>
                    No chart data available.
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
